import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  XMarkIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  PhotoIcon,
  ArrowsPointingOutIcon
} from '@heroicons/react/24/outline'
import PageHeader from '../components/PageHeader'
import ErrorBoundary from '../components/ErrorBoundary'

const GALLERY_ALBUMS = [
  {
    id: 'kigali-nnn-conference',
    title: '17th Annual NTD NGO Network Conference — Kigali, Rwanda',
    category: 'International Conferences',
    description: 'PACIEH CEO Prof. Chinyelu Angela Ekwunife representing the organization at the 17th Annual NNN Conference at Kigali Marriott Hotel, establishing strategic alliances with MITOSATH, DAHW Germany, University of Kigali, and the Neglected Tropical Diseases Community of Practice.',
    coverImage: '/images/KIGALI CONFERENCE/kigali-opening-ceremony.jpeg',
    images: [
      { src: '/images/KIGALI CONFERENCE/kigali-opening-ceremony.jpeg', caption: 'Opening Plenary Session of the 17th Annual NNN Conference at Kigali Marriott Hotel' },
      { src: '/images/KIGALI CONFERENCE/kigali-banner.jpeg', caption: 'Official 17th Annual NTD NGO Network Conference Banner: Towards 2030' },
      { src: '/images/KIGALI CONFERENCE/kigali-stage.jpeg', caption: 'Plenary Stage: Global Strategy for Eliminating Neglected Tropical Diseases' },
      { src: '/images/KIGALI CONFERENCE/kigali-mitosath-dahw.jpeg', caption: 'CEO Prof. C.A. Ekwunife in strategic consultation with Dr. Francisca Olamiju (MITOSATH) and Dr. Anil Fastenau (DAHW Germany)' },
      { src: '/images/KIGALI CONFERENCE/kigali-ppsn-members.jpeg', caption: 'Nigerian Delegation: Parasitology and Public Health Society of Nigeria (PPSN) leadership team' },
      { src: '/images/KIGALI CONFERENCE/kigali-university-of-kigali.jpeg', caption: 'Academic Collaboration: Prof. C.A. Ekwunife with Prof. Ogechi Adeola (DVC, University of Kigali)' },
      { src: '/images/KIGALI CONFERENCE/kigali-cop.jpeg', caption: 'Neglected Tropical Diseases Community of Practice (CoP) roundtable with Kate McCracken' },
      { src: '/images/KIGALI CONFERENCE/kigali-delegates-group.jpeg', caption: 'Pan-African and Global Public Health Delegates at Kigali Marriott Convention Centre' },
      { src: '/images/KIGALI CONFERENCE/kigali-networking-1.jpeg', caption: 'CEO Prof. Ekwunife engaging international health partners and research collaborators' },
      { src: '/images/KIGALI CONFERENCE/kigali-networking-2.jpeg', caption: 'Intersectoral discussions linking school feeding, deworming, and maternal care' },
      { src: '/images/KIGALI CONFERENCE/kigali-conference-session.jpeg', caption: 'Technical session on grassroots health delivery, community ownership, and NTD elimination' },
      { src: '/images/KIGALI CONFERENCE/kigali-session-hall.jpeg', caption: 'Delegates and researchers convened during the Kigali conference breakout sessions' },
      { src: '/images/KIGALI CONFERENCE/kigali-panel-discussion.jpeg', caption: 'Panel discussion exploring innovative health financing and maternal nutrition programs' },
    ]
  },
  {
    id: 'thai-ambassador',
    title: 'Visit of Thai Ambassador',
    category: 'Diplomatic & Partnerships',
    description: 'Documentation of the Royal Thai Ambassador’s visit to PACIEH facilities, local schools, and community elders.',
    coverImage: '/images/VISIT OF THAI AMBASSADOR/Visit-of-Thai-Ambassador-to-PACIEH-1.png',
    images: [
      { src: '/images/VISIT OF THAI AMBASSADOR/Visit-of-Thai-Ambassador-to-PACIEH-1.png', caption: 'Thai Ambassador visiting PACIEH administrative centre' },
      { src: '/images/VISIT OF THAI AMBASSADOR/Visit-of-Thai-Ambassador-to-PACIEH-2.png', caption: 'PACIEH team with the Thai Ambassadorial delegation' },
      { src: '/images/VISIT OF THAI AMBASSADOR/Visit-of-Ambassador.png', caption: 'Official briefing on community health achievements' },
      { src: '/images/VISIT OF THAI AMBASSADOR/Enugu-State-Governor-and-Thai-AMbassador-PACIEH.png', caption: 'Courtesy visit with the Executive Governor of Enugu State' },
      { src: '/images/VISIT OF THAI AMBASSADOR/Thai-Ambassador-in-class-PACIEH.png', caption: 'Ambassador interacting with pupils during classroom feeding' },
      { src: '/images/VISIT OF THAI AMBASSADOR/Traditional-leader-giving-a-gift-to-Thai-Ambassador-PACIEH.png', caption: 'Traditional community leader presenting commemorative gift' },
      { src: '/images/VISIT OF THAI AMBASSADOR/Community-women-are-PACIEH-partners.png', caption: 'Community women and volunteer cooks welcoming guests' },
      { src: '/images/VISIT OF THAI AMBASSADOR/Professor-Uche-Amazigo-PACIEH-Founder-speaking.png', caption: 'Founder Prof. Uche Amazigo detailing community innovations' },
    ]
  },
  {
    id: 'school-feeding',
    title: 'School Feeding & Nutrition Program',
    category: 'Nutrition & Education',
    description: 'Community-led school kitchens where local mothers prepare balanced, warm meals for enrolled pupils.',
    coverImage: '/images/SCHOOL FEEDING/School-children-eating-in-classroom-and-smiling-PACIEH.png',
    images: [
      { src: '/images/SCHOOL FEEDING/School-children-eating-in-classroom-and-smiling-PACIEH.png', caption: 'Smiling pupils enjoying fresh, hot school meals' },
      { src: '/images/SCHOOL FEEDING/Child-eating-PACIEH.png', caption: 'Nutritious lunch supporting child retention and active learning' },
      { src: '/images/SCHOOL FEEDING/Child-in-class-after-eating-PACIEH.png', caption: 'Energized students in class following mealtime' },
      { src: '/images/SCHOOL FEEDING/Plates-of-nutritious-meals-PACIEH.png', caption: 'Balanced, locally-sourced ingredients prepared for students' },
      { src: '/images/SCHOOL FEEDING/PACIEH-School-Cooks.png', caption: 'Dedicated mothers taking turns to cook in safe school kitchens' },
      { src: '/images/SCHOOL FEEDING/Hand-washing-with-soap-and-water-PACIEH.png', caption: 'Promoting hygiene: handwashing drills before every meal' },
      { src: '/images/SCHOOL FEEDING/School-children-eating-in-classroom-PACIEH.png', caption: 'Classroom meal session ensuring all children are nourished' },
    ]
  },
  {
    id: 'eye-health',
    title: 'School Eye Health Screening',
    category: 'Healthcare',
    description: 'Specialist eye examinations in remote primary schools, diagnosing refractive errors and childhood pathologies.',
    coverImage: '/images/EYE REPORTS/PACIEH-Eye-Screening-Program-Banner-1.png',
    images: [
      { src: '/images/EYE REPORTS/PACIEH-Eye-Screening-Program-Banner-1.png', caption: 'PACIEH Free Eye Screening and Child Vision Outreach' },
      { src: '/images/EYE REPORTS/Eye-Screening-Programme-at-Abomimi-Primary-School-Enugu-Ukwu-Njikoka-LGA-Anambra-State.png', caption: 'Vision screening at Abomimi Primary School, Enugu-Ukwu' },
      { src: '/images/EYE REPORTS/Eye-Screening-Program-at-Christ-Model-School-Achina-CMS-Aguata-LGA-Anambra-State.png', caption: 'Eye screening examination at Christ Model School, Achina' },
      { src: '/images/EYE REPORTS/Eye-Screening-Program-at-Dominic-Nursery-and-Primary-School-Anaocha-LGA-Anambra-State.png', caption: 'Optometrists examining young learners at St. Dominic School' },
      { src: '/images/EYE REPORTS/Eye-Screening-Program-at-Progressive-Primary-School-Achina-Aguata-LGA-Anambra-State.png', caption: 'Clinical documentation and vision testing at Achina' },
      { src: '/images/EYE REPORTS/Eye-Screening-Program-at-St.-Joseph-Primary-School-Ekwulobia-Aguata-LGA-Anambra-State.png', caption: 'Eye assessments at St. Joseph Primary School, Ekwulobia' },
    ]
  },
  {
    id: 'goodwill-medical',
    title: 'Goodwill Medical Centre (GMC)',
    category: 'Healthcare',
    description: 'Transforming health access for 500+ low-income families and bridging remote communities to emergency care.',
    coverImage: '/images/GOODWILL MEDICAL CENTRES/Goodwill-Medical-centre-front-view.jpeg',
    images: [
      { src: '/images/GOODWILL MEDICAL CENTRES/Goodwill-Medical-centre-front-view.jpeg', caption: 'Goodwill Medical Centre exterior front view in Umuchigbo' },
      { src: '/images/NEW AND MOMENT/tydf_inaugurates_goodwill_hospital_enugu.png', caption: 'Official commissioning and inauguration of GMC facility' },
      { src: '/images/MOVING IMPACT IMAGES/_Z4A9857.jpg', caption: 'Health workers and community engagement at the clinic' },
      { src: '/images/NEW AND MOMENT/PACIEH-NHGSFP2-400x284.jpg', caption: 'Community stakeholders collaborating on health access' },
    ]
  },
  {
    id: 'tba-training',
    title: 'Traditional Birth Attendants (TBAs)',
    category: 'Maternal Health',
    description: 'Training and geospatial mapping of 21 TBAs to reduce maternal and infant mortality across Enugu East.',
    coverImage: '/images/TBAS/IMG_9768.jpg',
    images: [
      { src: '/images/TBAS/IMG_9768.jpg', caption: 'TBA masterclass training session on safe delivery protocols' },
      { src: '/images/TBAS/IMG_9763.jpg', caption: 'Hands-on clinical orientation for rural traditional midwives' },
      { src: '/images/TBAS/IMG_9770.jpg', caption: 'Community discussion on referral networks to Goodwill Medical Centre' },
      { src: '/images/TBAS/IMG_9775.jpg', caption: 'Certificate and clean delivery kits distribution to trained TBAs' },
      { src: '/images/TBAS/IMG_9777.jpg', caption: 'Graduation cohort of empowered birth attendants' },
    ]
  },
  {
    id: 'deworming',
    title: 'Deworming & Preventative Health',
    category: 'Preventative Health',
    description: 'Targeted mass administration of Albendazole protecting school pupils from parasitic infections and stunting.',
    coverImage: '/images/DEWORMING 2014/Deworming-Tablet-PACIEH.jpg',
    images: [
      { src: '/images/DEWORMING 2014/Deworming-Tablet-PACIEH.jpg', caption: 'WHO-approved deworming treatment delivered directly to pupils' },
      { src: '/images/DEWORMING 2014/Albendazole-for-Deworming.jpg', caption: 'Albendazole tablets for safe school-wide administration' },
      { src: '/images/MOVING IMPACT IMAGES/B (10).jpg', caption: 'Health monitors overseeing pupil health routines' },
    ]
  },
  {
    id: 'field-impact',
    title: 'Field Moments & Community Spirit',
    category: 'Community',
    description: 'Capturing moments of hope, community leadership, and generational transformation across our field sites.',
    coverImage: '/images/MOVING IMPACT IMAGES/B (1).jpg',
    images: [
      { src: '/images/MOVING IMPACT IMAGES/B (1).jpg', caption: 'Children gathered during community celebration' },
      { src: '/images/MOVING IMPACT IMAGES/B (2).jpg', caption: 'Elders and mothers participating in community health assembly' },
      { src: '/images/MOVING IMPACT IMAGES/B (7).jpg', caption: 'Inspiring young minds with educational resources' },
      { src: '/images/MOVING IMPACT IMAGES/B (9).jpg', caption: 'Field volunteers working directly with local residents' },
      { src: '/images/MOVING IMPACT IMAGES/B (11).jpg', caption: 'Joy and resilience in target intervention communities' },
      { src: '/images/MOVING IMPACT IMAGES/d1.jpg', caption: 'Community outreach and sustainable development in action' },
    ]
  }
]

const CATEGORIES = ['All', 'International Conferences', 'Diplomatic & Partnerships', 'Nutrition & Education', 'Healthcare', 'Maternal Health', 'Community']

export default function Gallery() {
  const [selectedFilter, setSelectedFilter] = useState('All')
  const [activeAlbum, setActiveAlbum] = useState(null)
  const [lightboxState, setLightboxState] = useState({ isOpen: false, albumIndex: 0, photoIndex: 0 })

  const filteredAlbums = useMemo(() => {
    if (selectedFilter === 'All') return GALLERY_ALBUMS
    return GALLERY_ALBUMS.filter((a) => a.category === selectedFilter)
  }, [selectedFilter])

  // Open Lightbox directly on a photo
  const openLightbox = (album, photoIdx = 0) => {
    setActiveAlbum(album)
    setLightboxState({ isOpen: true, albumIndex: 0, photoIndex: photoIdx })
  }

  const closeLightbox = () => {
    setLightboxState({ isOpen: false, albumIndex: 0, photoIndex: 0 })
  }

  const nextPhoto = useCallback(() => {
    if (!activeAlbum) return
    setLightboxState((prev) => ({
      ...prev,
      photoIndex: (prev.photoIndex + 1) % activeAlbum.images.length
    }))
  }, [activeAlbum])

  const prevPhoto = useCallback(() => {
    if (!activeAlbum) return
    setLightboxState((prev) => ({
      ...prev,
      photoIndex: (prev.photoIndex - 1 + activeAlbum.images.length) % activeAlbum.images.length
    }))
  }, [activeAlbum])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxState.isOpen) return
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') nextPhoto()
      if (e.key === 'ArrowLeft') prevPhoto()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxState.isOpen, nextPhoto, prevPhoto])

  return (
    <ErrorBoundary>
      <Helmet>
        <title>Impact Gallery & Photos | PACIEH</title>
        <meta
          name="description"
          content="Explore visual stories and photo albums documenting PACIEH's community feeding, Goodwill Medical Centre, eye screening, and maternal health programs."
        />
      </Helmet>

      <PageHeader
        title="Impact Photo Gallery"
        subtitle="Visual stories from the ground — documenting our school kitchens, Goodwill Medical Centre, maternal health cohorts, and community partners across Africa."
      />

      <section className="py-16 bg-slate-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter Pills */}
          <div className="flex items-center justify-center gap-2 flex-wrap mb-12">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedFilter(cat)}
                className={`px-5 py-2.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                  selectedFilter === cat
                    ? 'bg-pacieh-blue text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-pacieh-green hover:text-pacieh-green'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Albums Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAlbums.map((album, idx) => (
              <motion.div
                key={album.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                onClick={() => openLightbox(album, 0)}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl hover:border-pacieh-green/50 transition-all duration-300 cursor-pointer flex flex-col"
              >
                {/* Cover Image Container */}
                <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                  <img
                    src={album.coverImage}
                    alt={album.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                  
                  {/* Photo count pill */}
                  <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[11px] font-semibold text-white flex items-center gap-1.5">
                    <PhotoIcon className="h-3.5 w-3.5" />
                    {album.images.length} Photos
                  </div>

                  {/* Category badge */}
                  <div className="absolute top-3 left-3 bg-pacieh-green/90 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                    {album.category}
                  </div>
                </div>

                {/* Content Details */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-pacieh-blue transition-colors mb-2">
                      {album.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                      {album.description}
                    </p>
                  </div>

                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-semibold text-pacieh-green group-hover:text-pacieh-blue">
                    <span className="flex items-center gap-1">
                      <ArrowsPointingOutIcon className="h-4 w-4" />
                      View Full Album
                    </span>
                    <span className="text-gray-400 group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox / Fullscreen Modal */}
      <AnimatePresence>
        {lightboxState.isOpen && activeAlbum && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col justify-between p-4 sm:p-6"
          >
            {/* Top Bar */}
            <div className="flex items-center justify-between text-white border-b border-white/10 pb-4">
              <div>
                <span className="text-xs font-medium text-pacieh-green uppercase tracking-wider block">
                  {activeAlbum.title}
                </span>
                <span className="text-sm text-white/70">
                  Photo {lightboxState.photoIndex + 1} of {activeAlbum.images.length}
                </span>
              </div>
              <button
                type="button"
                onClick={closeLightbox}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Photo View with Next/Prev */}
            <div className="relative flex-1 flex items-center justify-center my-4 select-none">
              {/* Prev Button */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                className="absolute left-2 sm:left-4 z-10 p-3 rounded-full bg-black/60 hover:bg-pacieh-green text-white transition-all shadow-lg"
                aria-label="Previous photo"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>

              {/* Current Image */}
              <motion.div
                key={lightboxState.photoIndex}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                className="max-h-[70vh] sm:max-h-[75vh] max-w-[92vw] flex flex-col items-center"
              >
                <img
                  src={activeAlbum.images[lightboxState.photoIndex]?.src}
                  alt={activeAlbum.images[lightboxState.photoIndex]?.caption || activeAlbum.title}
                  className="max-h-[65vh] sm:max-h-[70vh] max-w-full object-contain rounded-lg shadow-2xl"
                />
                {activeAlbum.images[lightboxState.photoIndex]?.caption && (
                  <p className="mt-4 text-center text-sm sm:text-base text-white/90 bg-black/50 px-4 py-2 rounded-lg max-w-2xl">
                    {activeAlbum.images[lightboxState.photoIndex]?.caption}
                  </p>
                )}
              </motion.div>

              {/* Next Button */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                className="absolute right-2 sm:right-4 z-10 p-3 rounded-full bg-black/60 hover:bg-pacieh-green text-white transition-all shadow-lg"
                aria-label="Next photo"
              >
                <ChevronRightIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Thumbnail Strip */}
            <div className="flex items-center justify-center gap-2 overflow-x-auto py-2 border-t border-white/10">
              {activeAlbum.images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightboxState((prev) => ({ ...prev, photoIndex: i }))}
                  className={`relative shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                    lightboxState.photoIndex === i
                      ? 'border-pacieh-green scale-105 ring-2 ring-pacieh-green/40'
                      : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img.src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ErrorBoundary>
  )
}